import { useCallback, useMemo, useState } from 'react';
import AssignmentAgency from '../listing/AssignmentAgency';
import AgencyAssignmentCreate from './AgencyAssignmentCreate';
import { Typography } from 'antd';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Assignment } from '@nxsys/store/slices/assignment/types';
import { AssignmentFormValues } from '../../asssignment/components/AssignmentFormStacked';

type Mode = 'list' | 'create';

const AgencyAssignmentTab = ({ agency }: { agency: IAgency }) => {
  const [mode, setMode] = useState<Mode>('list');
  const [initialValues, setInitialValues] =
    useState<Partial<AssignmentFormValues> | null>(null);
  const { messages } = useTypedIntl();

  const handleAddNew = useCallback(() => {
    setInitialValues(null);
    setMode('create');
  }, []);
  const handleDone = useCallback(() => setMode('list'), []);

  const handleViewDetail = useCallback((record: Assignment) => {
    const subcontractorRaw: any = (record as any)?.subcontractorRef;
    const subcontractorId =
      typeof subcontractorRaw === 'object' && subcontractorRaw
        ? (subcontractorRaw?.id ??
          subcontractorRaw?._id ??
          subcontractorRaw?.value)
        : subcontractorRaw;
    const subcontractorLabel =
      (record as any)?.subcontractorName ??
      (typeof subcontractorRaw === 'object' && subcontractorRaw
        ? (subcontractorRaw?.fullName ??
          subcontractorRaw?.name ??
          `${subcontractorRaw?.forename ?? ''} ${subcontractorRaw?.surname ?? ''}`.trim())
        : '') ??
      String(subcontractorId ?? '');

    setInitialValues({
      assignmentName: record?.assignmentName ?? '',
      clientRef: record?.clientRef ?? '',
      projectRef: record?.projectRef ?? '',
      startDate: record?.startDate ?? '',
      endDate: record?.endDate ?? '',
      status: record?.status
        ? { label: record.status as any, value: record.status as any }
        : null,
      employeeAndSubcontractor: subcontractorId
        ? {
            label: subcontractorLabel,
            value: subcontractorId,
          }
        : null,
      rates: Array.isArray(record?.rates)
        ? record.rates.map((r: any) => ({
            rateCode: r?.rateCode
              ? {
                  label: r.rateCode,
                  value: r.rateCode,
                  fullData: r,
                }
              : null,
            rateType: r?.rateType ?? '',
            description: r?.description ?? '',
            rate: r?.rate ?? '',
            vatDetails: r?.vatDetails
              ? { label: r.vatDetails, value: r.vatDetails }
              : null,
            vatRate: r?.vatRate ?? '',
          }))
        : undefined,
    });
    setMode('create');
  }, []);

  const content = useMemo(() => {
    if (mode === 'create') {
      return (
        <AgencyAssignmentCreate
          agency={agency}
          onBack={handleDone}
          onDone={handleDone}
          {...(initialValues ? { initialValues } : {})}
        />
      );
    }

    return (
      <div className="px-6 py-6 bg-white rounded-lg">
        <div className="mb-6 mt-4">
          <Typography.Text className="font-medium text-gray-800 text-2xl">
            {messages['common.assignment']}
          </Typography.Text>
        </div>
        <AssignmentAgency
          data={agency}
          hasWrapper={false}
          onAddNew={handleAddNew}
          onViewDetail={handleViewDetail}
        />
      </div>
    );
  }, [agency, handleAddNew, handleDone, handleViewDetail, initialValues, mode]);

  return <div>{content}</div>;
};

export default AgencyAssignmentTab;
